/**
 * Servizio Chat associati - lato Associato
 * Chiamate REST verso /api/chat/* con token OTP associato.
 * Ogni funzione restituisce i dati già estratti dalla risposta; gli errori
 * del backend ({ success:false, code, message }) si leggono con `erroreChat`.
 */

import axios from 'axios';
import { getAuthHeader } from './otpAuthService';
import { normalizzaCatalogo } from '../utils/chatSticker';
import type { CatalogoSticker } from '../utils/chatSticker';
import type {
  ChatAllegato,
  ChatCampiPersona,
  ChatContenutiRisposta,
  ChatContenutiTotali,
  ChatConversazione,
  ChatDestinazioneInoltro,
  ChatLettureMessaggio,
  ChatMenzionabile,
  ChatMessaggio,
  ChatPaginaImportanti,
  ChatPaginaMembri,
  ChatPaginaMessaggi,
  ChatPaginaRicerca,
  ChatPaginaRisposte,
  ChatReazioneAutore,
  ChatParametriRicerca,
  ChatRichiestaAlbum,
  ChatRisposta,
  ChatRispostaAlbum,
  ChatRispostaInoltro,
  ChatSchedaAssociato,
  ChatStato,
  EmojiReazione,
  TipoContenutoChat,
} from '../../../shared/types/chat';

// REST: usare REACT_APP_API_BASE_URL (include già /api), NON REACT_APP_API_URL (GraphQL).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const BASE = `${API_BASE_URL}/chat`;

/**
 * Token e `X-Chat-Client: web` su ogni chiamata: il server conta invio e lettura dal sito come
 * attività sulle schede visibili (spec notifiche §8). Sullo stesso dominio nessun preflight CORS
 * in più; in sviluppo `cors()` del backend riflette le intestazioni richieste.
 */
const conf = () => ({ headers: { ...(getAuthHeader() as any), 'X-Chat-Client': 'web' } });
const id$ = (id: string) => encodeURIComponent(id);

export type ChatProfilo = NonNullable<ChatStato['profilo']>;
/**
 * Voce dei bloccati: `id` è l'id persona. Piano 2 (campi facoltativi): `tuttoLoStudio` quando l'id è di uno studio
 * (il blocco vale per il titolare e tutti i suoi dipendenti), `persona` per il tipo (seconda riga = `rappresentanteLegale`).
 * `conDipendenti`: lo studio bloccato per intero ha dipendenti nella chat (solo allora il sito mostra «tutto lo studio»).
 */
export type ChatBloccato = { id: string; nome: string; sottotitolo: string; rappresentanteLegale?: string | null; tuttoLoStudio?: boolean; conDipendenti?: boolean } & ChatCampiPersona;

// --- Stato, regole, profilo ---

export const getStato = async (): Promise<ChatStato> => {
  const { data } = await axios.get(`${BASE}/stato`, conf());
  return {
    disponibile: data.disponibile,
    motivo: data.motivo,
    regole: data.regole,
    profilo: data.profilo ?? null,
    // backend precedente senza il campo: 0 (la card resta senza badge)
    nonLetti: typeof data.nonLetti === 'number' ? data.nonLetti : 0,
    // chi usa la chat (piano 2): solo se il backend lo manda
    ...(data.io && typeof data.io === 'object' ? { io: data.io } : {}),
  };
};

export const accettaRegole = async (versione: number): Promise<void> => {
  await axios.post(`${BASE}/regole/accetta`, { versione }, conf());
};

export const aggiornaProfilo = async (
  p: Partial<{ chiPuoScrivermi: ChatProfilo['chiPuoScrivermi']; mostraUltimoAccesso: boolean }>
): Promise<ChatProfilo> => {
  const { data } = await axios.put(`${BASE}/profilo`, p, conf());
  return data.profilo;
};

// --- Conversazioni ---

export const getConversazioni = async (): Promise<ChatConversazione[]> => {
  const { data } = await axios.get(`${BASE}/conversazioni`, conf());
  return data.conversazioni;
};

export const getConversazione = async (id: string): Promise<ChatConversazione> => {
  const { data } = await axios.get(`${BASE}/conversazioni/${id$(id)}`, conf());
  return data.conversazione;
};

export const apriDiretta = async (associatoId: string): Promise<ChatConversazione> => {
  const { data } = await axios.post(`${BASE}/conversazioni/dirette`, { associatoId }, conf());
  return data.conversazione;
};

/**
 * Pagina di messaggi. Con `da` (salto a un messaggio: mai insieme a `prima`) il server
 * risponde con la finestra contigua dal messaggio fino al più recente, oppure con
 * `troppoLontano: true` e nessun messaggio (presente solo con `da`).
 */
export const getMessaggi = async (
  id: string,
  opzioni: { prima?: string; limite?: number; da?: string } = {}
): Promise<ChatPaginaMessaggi> => {
  const params: Record<string, string | number> = {};
  if (opzioni.da) params.da = opzioni.da;
  else if (opzioni.prima) params.prima = opzioni.prima;
  if (opzioni.limite) params.limite = opzioni.limite;
  const { data } = await axios.get(`${BASE}/conversazioni/${id$(id)}/messaggi`, { ...conf(), params });
  const pagina: ChatPaginaMessaggi = { messaggi: data.messaggi || [], altri: !!data.altri, ricevute: data.ricevute ?? null };
  if (opzioni.da && typeof data.troppoLontano === 'boolean') pagina.troppoLontano = data.troppoLontano;
  if (typeof data.ora === 'string') pagina.ora = data.ora;
  return pagina;
};

// --- Ricerca nei messaggi (16/10/2026) ---

/** `q` 2-100 caratteri dopo `trim`; `prima` = `prossimo` della pagina precedente; `limite` 1-20. */
export const cercaMessaggi = async (p: ChatParametriRicerca): Promise<ChatPaginaRicerca> => {
  const params: Record<string, string | number> = { q: String(p.q || '').trim() };
  if (p.conversazioneId) params.conversazioneId = p.conversazioneId;
  if (p.prima) params.prima = p.prima;
  if (p.limite) params.limite = p.limite;
  const { data } = await axios.get(`${BASE}/ricerca`, { ...conf(), params });
  return { risultati: data.risultati || [], prossimo: data.prossimo ?? null };
};

/** Membri di un canale con `mostraMembri` (paginati, ordinati per nome; `q` cerca per nome). */
export const getMembriCanale = async (
  id: string,
  opzioni: { pagina?: number; limite?: number; q?: string } = {}
): Promise<ChatPaginaMembri> => {
  const params: Record<string, string | number> = {};
  if (opzioni.pagina) params.pagina = opzioni.pagina;
  if (opzioni.limite) params.limite = opzioni.limite;
  if (opzioni.q && opzioni.q.trim()) params.q = opzioni.q.trim();
  const { data } = await axios.get(`${BASE}/conversazioni/${id$(id)}/membri`, { ...conf(), params });
  return { membri: data.membri || [], pagina: data.pagina, limite: data.limite, totale: data.totale || 0 };
};

/**
 * Suggerimenti per le menzioni «@» in un canale (18/10/2026): al massimo 20, mai me stesso;
 * con l'elenco membri nascosto solo chi ha già scritto. `q` filtra per nome (vuoto = i primi 20).
 */
export const getMenzionabili = async (id: string, q: string): Promise<ChatMenzionabile[]> => {
  const params: Record<string, string> = {};
  const testo = String(q || '').trim();
  if (testo) params.q = testo;
  const { data } = await axios.get(`${BASE}/conversazioni/${id$(id)}/menzionabili`, { ...conf(), params });
  return Array.isArray(data.associati) ? data.associati : [];
};

/** `stickerId` (25/10/2026): sticker del catalogo, senza `testo` né `allegatoId`. */
export const inviaMessaggio = async (
  id: string,
  corpo: { clientId: string; testo?: string; allegatoId?: string; stickerId?: string; rispostaA?: string }
): Promise<ChatMessaggio> => {
  const { data } = await axios.post(`${BASE}/conversazioni/${id$(id)}/messaggi`, corpo, conf());
  return data.messaggio;
};

export const segnaLetto = async (id: string): Promise<string | null> => {
  const { data } = await axios.post(`${BASE}/conversazioni/${id$(id)}/letto`, {}, conf());
  return data.lettoFino ?? null;
};

export const aggiornaPreferenze = async (
  id: string,
  preferenze: { silenziataFino?: string | 'sempre' | null; fissata?: boolean; archiviata?: boolean }
): Promise<ChatConversazione> => {
  const { data } = await axios.patch(`${BASE}/conversazioni/${id$(id)}/preferenze`, preferenze, conf());
  return data.conversazione;
};

export const eliminaChat = async (id: string): Promise<void> => {
  await axios.delete(`${BASE}/conversazioni/${id$(id)}`, conf());
};

// --- Messaggi ---

export const modificaMessaggio = async (id: string, testo: string): Promise<ChatMessaggio> => {
  const { data } = await axios.patch(`${BASE}/messaggi/${id$(id)}`, { testo }, conf());
  return data.messaggio;
};

export const eliminaMessaggio = async (id: string, perTutti: boolean): Promise<void> => {
  await axios.delete(`${BASE}/messaggi/${id$(id)}`, { ...conf(), params: perTutti ? { perTutti: '1' } : {} });
};

export const reagisci = async (id: string, emoji: EmojiReazione | null): Promise<ChatMessaggio> => {
  const { data } = await axios.put(`${BASE}/messaggi/${id$(id)}/reazione`, { emoji }, conf());
  return data.messaggio;
};

/** Chi ha messo le reazioni a un messaggio (19/10/2026): la mia per prima. */
export const getReazioni = async (id: string): Promise<ChatReazioneAutore[]> => {
  const { data } = await axios.get(`${BASE}/messaggi/${id$(id)}/reazioni`, conf());
  return Array.isArray(data.reazioni) ? data.reazioni : [];
};

/** «Info messaggio» di un mio messaggio in un canale (Task 16): letto da, consegnato a, restanti. */
export const getLetture = async (id: string): Promise<ChatLettureMessaggio> => {
  const { data } = await axios.get(`${BASE}/messaggi/${id$(id)}/letture`, conf());
  return {
    letto: Array.isArray(data.letto) ? data.letto : [],
    consegnato: Array.isArray(data.consegnato) ? data.consegnato : [],
    restanti: Number(data.restanti) || 0,
    totali: { letto: 0, consegnato: 0, membri: 0, ...(data.totali || {}) },
    mostraMembri: !!data.mostraMembri,
  };
};

export const segnala = async (id: string, motivo: string, nota?: string): Promise<{ gia: boolean }> => {
  const { data } = await axios.post(`${BASE}/messaggi/${id$(id)}/segnala`, { motivo, nota }, conf());
  return { gia: !!data.gia };
};

/**
 * Inoltro a 1-5 destinazioni (16/10/2026): sempre 200 dopo la validazione del corpo,
 * con un esito per destinazione nello stesso ordine. Lo stesso `clientId` nei
 * tentativi successivi non duplica.
 */
export const inoltraMessaggio = async (id: string, destinazioni: ChatDestinazioneInoltro[]): Promise<ChatRispostaInoltro> => {
  const { data } = await axios.post(`${BASE}/messaggi/${id$(id)}/inoltra`, { destinazioni }, conf());
  return { esiti: data.esiti || [] };
};

/** Album di 2-10 foto già caricate (16/10/2026): 201 con foto nuove, 200 se già inviate tutte. */
export const inviaAlbum = async (id: string, corpo: ChatRichiestaAlbum): Promise<ChatRispostaAlbum> => {
  const elementi = corpo.elementi.map((e) => (e.testo ? { clientId: e.clientId, allegatoId: e.allegatoId, testo: e.testo } : { clientId: e.clientId, allegatoId: e.allegatoId }));
  const { data } = await axios.post(`${BASE}/conversazioni/${id$(id)}/album`, { album: corpo.album, elementi }, conf());
  return { album: data.album, nuovi: typeof data.nuovi === 'number' ? data.nuovi : 0, messaggi: data.messaggi || [] };
};

// --- Risposte agli annunci (04/11/2026) ---

/** Risposte di un annuncio che posso vedere, dalla più vecchia; `dopo` = id dell'ultima voce della pagina precedente. */
export const getRisposte = async (radiceId: string, opzioni: { dopo?: string } = {}): Promise<ChatPaginaRisposte> => {
  const params: Record<string, string> = {};
  if (opzioni.dopo) params.dopo = opzioni.dopo;
  const { data } = await axios.get(`${BASE}/messaggi/${id$(radiceId)}/risposte`, { ...conf(), params });
  return {
    radice: data.radice,
    risposte: Array.isArray(data.risposte) ? data.risposte : [],
    altri: !!data.altri,
    visibilita: data.visibilita === 'solo_unasca' ? 'solo_unasca' : 'tutti',
    puoRispondere: !!data.puoRispondere,
    motivoNonRisponde: data.motivoNonRisponde ?? null,
  };
};

/** Come `inviaMessaggio`: idempotente con `clientId` (lo stesso `clientId` restituisce la risposta già salvata). */
export const inviaRisposta = async (
  radiceId: string,
  corpo: { clientId: string; testo?: string; allegatoId?: string; stickerId?: string }
): Promise<ChatRisposta> => {
  const { data } = await axios.post(`${BASE}/messaggi/${id$(radiceId)}/risposte`, corpo, conf());
  return data.risposta;
};

export const modificaRisposta = async (id: string, testo: string): Promise<ChatRisposta> => {
  const { data } = await axios.put(`${BASE}/risposte/${id$(id)}`, { testo }, conf());
  return data.risposta;
};

/** Eliminazione per tutti (l'unica): resta il segnaposto, che continua a contare tra le risposte. */
export const eliminaRisposta = async (id: string): Promise<ChatRisposta> => {
  const { data } = await axios.delete(`${BASE}/risposte/${id$(id)}`, conf());
  return data.risposta;
};

export const reagisciRisposta = async (id: string, emoji: EmojiReazione | null): Promise<ChatRisposta> => {
  const { data } = await axios.put(`${BASE}/risposte/${id$(id)}/reazione`, { emoji }, conf());
  return data.risposta;
};

export const segnalaRisposta = async (id: string, motivo: string, nota?: string): Promise<{ gia: boolean }> => {
  const { data } = await axios.post(`${BASE}/risposte/${id$(id)}/segnala`, { motivo, nota }, conf());
  return { gia: !!data.gia };
};

// --- Messaggi importanti (18/10/2026) ---

/** Mette (`true`) o toglie (`false`) la stella; idempotente. Restituisce il valore confermato dal server. */
export const segnaImportante = async (id: string, importante: boolean): Promise<boolean> => {
  const url = `${BASE}/messaggi/${id$(id)}/importante`;
  const { data } = importante ? await axios.put(url, {}, conf()) : await axios.delete(url, conf());
  return typeof data.importante === 'boolean' ? data.importante : importante;
};

/**
 * Messaggi importanti, dalla stella più recente: di tutte le chat o di una sola (`conversazioneId`).
 * `prima` = `prossimo` della pagina precedente; `limite` 1-50.
 */
export const getImportanti = async (
  opzioni: { conversazioneId?: string; prima?: string; limite?: number } = {}
): Promise<ChatPaginaImportanti> => {
  const params: Record<string, string | number> = {};
  if (opzioni.conversazioneId) params.conversazione = opzioni.conversazioneId;
  if (opzioni.prima) params.prima = opzioni.prima;
  if (opzioni.limite) params.limite = opzioni.limite;
  const { data } = await axios.get(`${BASE}/importanti`, { ...conf(), params });
  return { messaggi: Array.isArray(data.messaggi) ? data.messaggi : [], prossimo: data.prossimo ?? null };
};

/** Risposta di `/contenuti` (associato e admin) con i campi mancanti ai valori neutri. */
export const normalizzaContenuti = (data: any): ChatContenutiRisposta => ({
  // gli sticker non sono media della chat (spec sticker §5.2), anche se un server li mandasse
  voci: data && Array.isArray(data.voci) ? data.voci.filter((v: any) => !v || String(v.tipo) !== 'sticker') : [],
  prossimo: (data && data.prossimo) || null,
  ...(data && data.totali ? { totali: data.totali } : {}),
});

/** Totale dei contenuti: il server conta al più 1000 per tipo, oltre si mostra «999+». */
export const numeroContenuti = (n: number): string => (n >= 1000 ? '999+' : String(n));

/** Cache in memoria dei totali (5 minuti): li chiede solo la scheda info, una volta. */
export const DURATA_CACHE_TOTALI_MS = 5 * 60 * 1000;
export const creaCacheTotali = (leggi: (id: string) => Promise<ChatContenutiRisposta>) => {
  const cache = new Map<string, { totali: ChatContenutiTotali | null; il: number }>();
  const funzione = async (id: string, ora: number = Date.now()): Promise<ChatContenutiTotali | null> => {
    const c = cache.get(id);
    if (c && ora - c.il < DURATA_CACHE_TOTALI_MS) return c.totali;
    const r = await leggi(id);
    const totali = r.totali || null;
    // un conteggio non riuscito (null) non si tiene: la prossima apertura riprova
    if (totali) cache.set(id, { totali, il: ora });
    return totali;
  };
  return Object.assign(funzione, { svuota: () => cache.clear() });
};

/**
 * «Media, documenti e link» di una chat (20/10/2026): una pagina del tipo scelto, dal più recente.
 * `prima` = `prossimo` della pagina precedente; `totali` solo con `totali: true` nella prima pagina.
 */
export const getContenuti = async (
  conversazioneId: string,
  tipo: TipoContenutoChat,
  opzioni: { prima?: string; limite?: number; totali?: boolean } = {}
): Promise<ChatContenutiRisposta> => {
  const params: Record<string, string | number> = { tipo };
  if (opzioni.prima) params.prima = opzioni.prima;
  if (opzioni.limite) params.limite = opzioni.limite;
  if (opzioni.totali) params.totali = 1;
  const { data } = await axios.get(`${BASE}/conversazioni/${id$(conversazioneId)}/contenuti`, { ...conf(), params });
  return normalizzaContenuti(data);
};

/** Totali della chat per la scheda info (cache di 5 minuti; null se il server non riesce a contarli). */
export const getTotaliContenuti = creaCacheTotali((id) => getContenuti(id, 'media', { limite: 1, totali: true }));

// --- Sticker (25/10/2026) ---

/**
 * Catalogo degli sticker che posso inviare in una conversazione (vuoto se non posso). Un backend
 * senza sticker risponde 404: chi chiama tratta ogni errore come catalogo vuoto.
 */
export const getCatalogoSticker = async (conversazioneId: string): Promise<CatalogoSticker> => {
  const { data } = await axios.get(`${BASE}/sticker`, { ...conf(), params: { conversazione: conversazioneId } });
  return normalizzaCatalogo(data);
};

// --- Allegati ---

export const caricaAllegato = async (file: File, onProgress?: (p: number) => void): Promise<ChatAllegato> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`${BASE}/allegati`, form, {
    ...conf(),
    onUploadProgress: (e: any) => {
      if (onProgress && e && e.total) onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
    },
  });
  return data.allegato;
};

/**
 * Toglie un mio allegato caricato e mai inviato (album o anteprima annullati, 16/10/2026):
 * il server risponde 404 se è già collegato a un messaggio, se non è mio o non esiste.
 * Da chiamare «al meglio»: un errore non deve mai bloccare chi annulla.
 */
export const eliminaAllegato = async (id: string): Promise<void> => {
  await axios.delete(`${BASE}/allegati/${id$(id)}`, conf());
};

/** `eliminaAllegato` senza attese né errori (vecchi backend senza la rotta compresi). */
export const scartaAllegato = (id: string | null | undefined): void => {
  if (!id) return;
  eliminaAllegato(id).catch(() => {
    // il server pulisce comunque gli allegati mai inviati dopo 24 ore
  });
};

// --- Associati e blocchi ---

export const cercaAssociati = async (q: string): Promise<ChatSchedaAssociato[]> => {
  const { data } = await axios.get(`${BASE}/associati`, { ...conf(), params: { q } });
  return data.associati;
};

export const getAssociato = async (id: string): Promise<ChatSchedaAssociato> => {
  const { data } = await axios.get(`${BASE}/associati/${id$(id)}`, conf());
  return data.associato;
};

export const getBloccati = async (): Promise<ChatBloccato[]> => {
  const { data } = await axios.get(`${BASE}/bloccati`, conf());
  return data.bloccati;
};

export const blocca = async (id: string): Promise<void> => {
  await axios.post(`${BASE}/bloccati/${id$(id)}`, {}, conf());
};

export const sblocca = async (id: string): Promise<void> => {
  await axios.delete(`${BASE}/bloccati/${id$(id)}`, conf());
};

// --- Supporto ---

/**
 * Gli URL firmati e gli avatar arrivano di solito relativi (`/api/chat/...`,
 * `/uploads/...`): si antepone l'origine del backend. Quelli già assoluti
 * (`http://`, `https://`) restano invariati.
 */
export const urlAssoluto = (percorso: string): string => {
  if (/^https?:\/\//i.test(percorso)) return percorso;
  return API_BASE_URL.replace(/\/api\/?$/, '') + (percorso.startsWith('/') ? percorso : `/${percorso}`);
};

export interface ErroreChat { codice: string | null; messaggio: string; status: number | null }

export const erroreChat = (err: any): ErroreChat => {
  const risposta = err && err.response;
  if (!risposta) return { codice: 'RETE', messaggio: 'Connessione assente. Riprova.', status: null };
  const data = risposta.data || {};
  return {
    codice: typeof data.code === 'string' ? data.code : null,
    messaggio: typeof data.message === 'string' && data.message ? data.message : 'Si è verificato un errore. Riprova.',
    status: typeof risposta.status === 'number' ? risposta.status : null,
  };
};

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';

export const nuovoClientId = (): string => {
  const byte = new Uint8Array(24);
  const c: Crypto | undefined = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(byte);
  } else {
    for (let i = 0; i < byte.length; i++) byte[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < byte.length; i++) out += ALFABETO.charAt(byte[i] & 63);
  return out;
};
